<template>
  <div id="security" class="flex flex-col gap-10 proxima_nova">
    <div id="security_header" class="flex flex-row items-center gap-3 px-[8vw]">
      <h1 class="text-xl font-semibold text_color">Single Sign On</h1>
      <StatusTagProvider :text="ssoData.enabled ? 'Enabled' : 'Disabled'" />
    </div>

    <div class="flex flex-col justify-between">
      <div
        id="security_body"
        class="flex flex-col gap-10 w-2/3 overflow-y-scroll pb-4 px-[8vw]"
        style="height: calc(100vh - (240px + var(--banner-height)))"
      >
        <div id="status" class="flex flex-col gap-1">
          <span class="text-[13px] secondary_color">Status</span>

          <a-select v-model:value="ssoData.status" @change="handleStatusChange">
            <template #suffixIcon>
              <ThemeImage
                :icon="imageNames.BOTTOM_ARROW"
                alt="bottom_white_arrow"
                class="size-4"
              />
            </template>

            <a-select-option
              v-for="option in singleSignOnStatusOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </a-select-option>
          </a-select>
        </div>

        <div class="flex flex-col gap-6">
          <div class="flex flex-col gap-1">
            <h2 class="text-base text_color font-semibold">SSO integration</h2>
            <span class="secondary_color text-[13px]">
              Configure your identity provider details to allow your team to
              access SuperAGI via Single Sign On
            </span>
          </div>

          <div class="flex flex-col gap-6">
            <div id="single_sign_on_url" class="flex flex-col gap-1">
              <span class="text-[13px] secondary_color"
              >SuperAGI single sign on URL</span
              >

              <div class="flex flex-row items-center gap-1">
                <a-input
                  v-model:value="ssoData.url"
                  class="input_medium"
                  placeholder="Enter URL"
                  disabled
                  :value="generateSSOUrl()"
                />

                <a-button
                  class="secondary_medium !px-2"
                  @click="copyToClipboard(generateSSOUrl())"
                >
                  <ThemeImage
                    :icon="imageNames.COPY_GREY"
                    alt="copy"
                    class="size-4"
                  />
                </a-button>
              </div>
            </div>

            <div
              id="identity_provider_single_sign_on_url"
              class="flex flex-col gap-1"
            >
              <span class="text-[13px] secondary_color"
              >Identity provider single sign on URL</span
              >

              <a-input
                v-model:value="ssoData.idpSsoUrl"
                class="input_medium"
                placeholder="Enter URL"
                @change="handleConfigChange"
              />
            </div>

            <div id="identity_provider_certificate" class="flex flex-col gap-1">
              <span class="text-[13px] secondary_color"
              >Identity provider certificate</span
              >

              <a-textarea
                v-model:value="ssoData.idpCertificate"
                class="input_medium"
                :placeholder="certificatePlaceholder"
                :rows="8"
                @change="handleConfigChange"
              />

              <div class="flex items-center gap-2 text-[11px] secondary_color">
                <span
                >Certificate fingerprint will be calculated
                  automatically</span
                >
                <span
                  v-if="ssoData.calculatedFingerprint"
                  class="text-green-600"
                >
                  ✓ {{ ssoData.calculatedFingerprint }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        id="security_footer"
        class="flex flex-row items-center justify-end gap-2 py-6 px-10 md:px-[6vw] border_top"
      >
        <a-button
          class="secondary_medium"
          @click="testConfiguration"
          :loading="ssoData.testing"
          :disabled="!isConfigurationValid"
        >
          Test Configuration
        </a-button>

        <a-button
          class="primary_medium"
          :loading="ssoData.saving"
          :disabled="!ssoData.hasChanges"
          @click="saveConfiguration"
        >
          Save changes
        </a-button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from "vue";
import { message } from "ant-design-vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import DashboardService from "@/src/api/DashboardService.js";
import ThemeImage from "@/src/components/ThemeImage.vue";
import StatusTagProvider from "@/src/components/StatusTagProvider.vue";
import {
  certificatePlaceholder,
  singleSignOnStatusOptions,
} from "@/src/constants/securityConstants.js";
import { copyToClipboard } from "@/src/utils/utils.js";

const ssoData = ref({
  enabled: false,
  status: "disabled",
  identityProvider: "",
  url: "",
  idpSsoUrl: "",
  idpCertificate: "",
  calculatedFingerprint: "",
  saving: false,
  testing: false,
  hasChanges: false,
  existingConfigId: null,
});

const originalConfig = ref({});

const resetSSOData = () => {
  ssoData.value.existingConfigId = null;
  ssoData.value.status = "disabled";
  ssoData.value.enabled = false;
  ssoData.value.identityProvider = "";
  ssoData.value.idpSsoUrl = "";
  ssoData.value.idpCertificate = "";
  ssoData.value.calculatedFingerprint = "";
};

const updateOriginalConfig = () => {
  originalConfig.value = {
    status: ssoData.value.status,
    identityProvider: ssoData.value.identityProvider,
    idpSsoUrl: ssoData.value.idpSsoUrl,
    idpCertificate: ssoData.value.idpCertificate,
  };
};

const isConfigurationValid = computed(() => {
  return ssoData.value.idpSsoUrl && ssoData.value.idpCertificate;
});

const generateSSOUrl = () => {
  try {
    const baseUrl = window.location.origin;
    return `${baseUrl}/auth_users/saml/callback`;
  } catch (error) {
    console.error("An error occurred while generating SSO URL: ", error);
    return "";
  }
};

const handleStatusChange = (value) => {
  try {
    ssoData.value.enabled = value === "enabled";
    checkForChanges();
  } catch (error) {
    console.error("An error occurred while handling status change: ", error);
  }
};

const handleConfigChange = () => {
  try {
    checkForChanges();
  } catch (error) {
    console.error("An error occurred while handling config change: ", error);
  }
};

const checkForChanges = () => {
  try {
    const current = {
      status: ssoData.value.status,
      identityProvider: ssoData.value.identityProvider,
      idpSsoUrl: ssoData.value.idpSsoUrl,
      idpCertificate: ssoData.value.idpCertificate,
    };

    ssoData.value.hasChanges =
      JSON.stringify(current) !== JSON.stringify(originalConfig.value);
  } catch (error) {
    console.error("An error occurred while checking for changes: ", error);
  }
};

const saveConfiguration = async () => {
  try {
    ssoData.value.saving = true;

    const configData = {
      domain: window.location.hostname.split(".")[0] || "default",
      idp_sso_target_url: ssoData.value.idpSsoUrl,
      idp_cert: ssoData.value.idpCertificate,
      name_identifier_format:
        "urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress",
      active: ssoData.value.status === "enabled",
      attribute_mapping: {
        email: "email",
        first_name: "first_name",
        last_name: "last_name",
        name: "name",
      },
    };

    let response;
    if (ssoData.value.existingConfigId) {
      response = await DashboardService.updateSSOConfiguration(
        ssoData.value.existingConfigId,
        configData,
      );
    } else {
      response = await DashboardService.saveSSOConfiguration(configData);
    }

    if (response.success) {
      message.success("SSO configuration saved successfully");

      if (response.saml_configuration && response.saml_configuration.id) {
        ssoData.value.existingConfigId = response.saml_configuration.id;
      }

      if (
        response.saml_configuration &&
        response.saml_configuration.idp_cert_fingerprint
      ) {
        ssoData.value.calculatedFingerprint =
          response.saml_configuration.idp_cert_fingerprint;
      }

      updateOriginalConfig();
      ssoData.value.hasChanges = false;
    } else {
      message.error(response.error || "Failed to save SSO configuration");
    }
  } catch (error) {
    console.error("An error occurred while saving SSO configuration: ", error);
    message.error("Failed to save SSO configuration");
  } finally {
    ssoData.value.saving = false;
  }
};

const testConfiguration = async () => {
  try {
    ssoData.value.testing = true;

    const configData = {
      idp_sso_target_url: ssoData.value.idpSsoUrl,
      idp_cert: ssoData.value.idpCertificate,
    };

    const response = await DashboardService.testSSOConfiguration(configData);

    if (response.success) {
      message.success("SSO configuration test successful");

      if (response.fingerprint) {
        ssoData.value.calculatedFingerprint = response.fingerprint;
      }
    } else {
      message.error(response.error || "SSO configuration test failed");
    }
  } catch (error) {
    console.error("An error occurred while testing SSO configuration: ", error);
    message.error(error.response.data.error || "SSO configuration test failed");
  } finally {
    ssoData.value.testing = false;
  }
};

const fetchSSOConfiguration = async () => {
  try {
    const response = await DashboardService.getSSOConfiguration();

    if (response.success && response.saml_configuration) {
      const config = response.saml_configuration;

      ssoData.value.existingConfigId = config.id;
      ssoData.value.status = config.active ? "enabled" : "disabled";
      ssoData.value.enabled = config.active;
      ssoData.value.idpSsoUrl = config.idp_sso_target_url || "";
      ssoData.value.idpCertificate = config.idp_cert || "";
      ssoData.value.calculatedFingerprint = config.idp_cert_fingerprint || "";
      ssoData.value.url = config.assertion_consumer_service_url;
    } else {
      resetSSOData();
    }

    updateOriginalConfig();
  } catch (error) {
    console.error(
      "An error occurred while fetching SSO configuration: ",
      error,
    );
    resetSSOData();
    updateOriginalConfig();
  }
};

const calculateFingerprint = async (certificate) => {
  try {
    if (
      certificate &&
      certificate.includes("-----BEGIN CERTIFICATE-----") &&
      certificate.includes("-----END CERTIFICATE-----")
    ) {
      const response = await DashboardService.testSSOConfiguration({
        idp_sso_target_url:
          ssoData.value.idpSsoUrl || "https://example.com/sso",
        idp_cert: certificate,
      });

      if (response.success && response.fingerprint) {
        ssoData.value.calculatedFingerprint = response.fingerprint;
      } else {
        ssoData.value.calculatedFingerprint = "";
      }
    } else {
      ssoData.value.calculatedFingerprint = "";
    }
  } catch (error) {
    console.error("An error occurred while calculating fingerprint: ", error);
    ssoData.value.calculatedFingerprint = "";
  }
};

let fingerprintTimeout;
watch(
  () => ssoData.value.idpCertificate,
  (newCert) => {
    try {
      if (fingerprintTimeout) {
        clearTimeout(fingerprintTimeout);
      }

      fingerprintTimeout = setTimeout(() => {
        calculateFingerprint(newCert);
      }, 1000);
    } catch (error) {
      console.error(
        "An error occurred while watching certificate changes: ",
        error,
      );
    }
  },
);

onMounted(() => {
  try {
    fetchSSOConfiguration();
  } catch (error) {
    console.error("An error occurred during component mount: ", error);
  }
});
</script>
